import { MobileOutlined } from '@ant-design/icons';
import { Checkbox, Col, Row } from 'antd';
import { filter, forEach, map, nth, slice } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMedia } from 'react-use';
import { Timezone } from '../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../common/CommonUseLazyQuery';
import {
  APPROVAL_TYPE,
  BREAKPOINTS,
  DATETIMEWITHDIVIDE,
  DEFAULT_PAGE_SIZE,
} from '../../../common/constants';
import useRouter from '../../../common/useRouter';
import { removeHistoryStateData } from '../../../common/utils';
import CommonCard from '../../../components/CommonCard';
import CommonDropdown from '../../../components/CommonDropdown';
import CommonTable from '../../../components/CommonTable';
import CommonTooltip from '../../../components/CommonTooltip';
import EllipsisText from '../../../components/EllipsisText';
import InfiniteScrollHandler from '../../../components/InfiniteScrollHandler';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import { GET_PROJECT_DROPDOWN_LIST } from '../../projects/graphql/Queries';
import { GET_USER_EQC_APPROVAL_LOGS } from '../graphql/Queries';
import ThankyouModal from './ThankyouModal';

const TodoList = () => {
  const initialTodoFilter = {
    skip: 0,
    limit: 10,
    sortBy: { field: 'updatedAt', order: 'DESC' },
    isRfi: false,
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [approvalListData, setApprovalListData] = useState();
  const [sortedInfo, setSortedInfo] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [todoListFilter, setTodoListFilter] = useState(initialTodoFilter);
  const [showModal, setShowModal] = useState(false);
  const { navigate, location } = useRouter();
  const [isRfi, setIsRfi] = useState(todoListFilter?.isRfi);

  const [fetchTodoData, { loading }] = useLazyQueryWithCallbacks(
    GET_USER_EQC_APPROVAL_LOGS,
    {
      variables: { filter: todoListFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.getUserEqcApprovalLogs?.data;
        const pagination = {
          ...paginationProp,
          total: res?.getUserEqcApprovalLogs?.total,
        };
        if (scrollFlag) {
          const datalist = [...approvalListData, ...data];
          setApprovalListData(datalist);
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setApprovalListData(datalist);
        }
        setHasMore(!!data?.length);
        setPaginationProp(pagination);
      },
      onError() {},
    },
  );

  useEffect(() => {
    if (location?.state?.showThankyouModal) {
      setShowModal(true);
    }
  }, [location?.state?.showThankyouModal]);

  useEffect(() => {
    fetchTodoData({ variables: { filter: todoListFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pagination, filterData, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setTodoListFilter({
        ...todoListFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: {
          field: sorter.columnKey,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      fetchTodoData({
        variables: {
          filter: {
            ...todoListFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: {
              field: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            },
          },
        },
      });
    } else {
      setTodoListFilter({
        ...todoListFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: { field: 'updatedAt', order: 'DESC' },
      });
      fetchTodoData({
        variables: {
          filter: {
            ...todoListFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: { field: 'updatedAt', order: 'DESC' },
          },
        },
      });
    }
  };

  const getStageNameAndStatus = (eqcStageHistory) => {
    const { name, levelName } = eqcStageHistory;
    return (
      <div id="stage-name-status">
        <span id="stage-name">{name || '-'}</span>
        <div id="level-name">({levelName})</div>
      </div>
    );
  };

  const getApproverName = (values, record) => {
    const fetchName = [];
    const { eqcStageApprovals } = record?.eqcStage;
    forEach(
      filter(
        eqcStageApprovals,
        (data) => data?.levelNumber === record?.eqcStageHistory?.levelNumber,
      ),
      (approver) => {
        const {
          externalUserApproverName: extApprName,
          internalApproverName: intApprName,
        } = approver;
        if (intApprName || extApprName) {
          fetchName.push(intApprName || extApprName);
        }
      },
    );
    const selectedName = nth(fetchName, 0);
    if (fetchName?.length === 1) {
      return (
        <CommonTooltip
          title={selectedName}
          placement={isDesktopViewport ? 'right' : 'top'}
        >
          {selectedName}
        </CommonTooltip>
      );
    }
    if (fetchName?.length > 1) {
      return (
        <CommonTooltip
          title={slice(fetchName, 1, fetchName?.length).join(', ')}
          placement={isDesktopViewport ? 'right' : 'top'}
        >
          {`${selectedName} and +${fetchName?.length - 1}`}
        </CommonTooltip>
      );
    }
    return '-';
  };

  const getNextStage = (record) => {
    if (
      !record?.eqcStageHistory?.inspectorReminderDate ||
      record?.eqcStageHistory?.isRfiStage
    ) {
      return '-';
    }
    const isOverDue =
      moment(record?.eqcStageHistory?.inspectorReminderDate) < moment();
    return (
      <div className={isOverDue ? 'text-danger' : ''} id="next-stage-info">
        {isOverDue ? `Due since` : `Due in`}
        {` ${moment(record?.eqcStageHistory?.inspectorReminderDate).from(
          moment(),
          true,
        )}`}
        <div id="reminder-date-info">
          {moment(record?.eqcStageHistory?.inspectorReminderDate)
            ?.tz(record?.eqc?.project?.timeZone)
            ?.format(DATETIMEWITHDIVIDE)}
          <div className="d-flex align-center timezone-div" id="timezone-info">
            <Timezone title="timeZone" id="timezone-icon" />
            {record?.eqc?.project?.timeZone}
          </div>
        </div>
      </div>
    );
  };
  const columns = [
    {
      title: '#',
      key: 'id',
      render: (text, record, index) => {
        return (
          <div id={`todo-index-${index}`}>
            {todoListFilter?.skip + index + 1}
          </div>
        );
      },
    },
    {
      title: 'PROJECT',
      dataIndex: 'projectName',
      key: 'projectName',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'projectName' && sortedInfo?.order,
      render: (text, record, index) => (
        <div id={`project-name-${index}`}>
          <EllipsisText text={record?.eqc?.project?.name} />
        </div>
      ),
    },
    {
      title: 'LOCATION',
      dataIndex: 'eqcName',
      key: 'eqcName',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'eqcName' && sortedInfo?.order,
      render: (text, record, index) => (
        <div id={`eqc-name-${index}`}>
          <EllipsisText text={record?.eqc?.eqcName} />
        </div>
      ),
    },
    {
      title: 'CHECKLIST',
      dataIndex: 'projectEqcTypeName',
      key: 'projectEqcTypeName',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'projectEqcTypeName' && sortedInfo?.order,
      render: (text, record, index) => (
        <div id={`eqc-type-${index}`}>
          <EllipsisText text={record?.eqc?.projectEqcTypeName || '-'} />
        </div>
      ),
    },
    {
      title: 'STAGE(Approval Pending)',
      key: 'eqcStageHistory',
      dataIndex: 'eqcStageHistory',
      render: (text) => {
        return getStageNameAndStatus(text);
      },
    },
    {
      title: 'APPROVERS',
      key: 'approver',
      dataIndex: 'approver',
      render: (values, record) => getApproverName(values, record),
    },
    {
      title: 'REQUESTED BY',
      key: 'approvalSender',
      dataIndex: 'approvalSender',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'approvalSender' && sortedInfo?.order,
      render: (text, record) => record?.approvalSender?.name || '-',
    },
    {
      title: 'NEXT STAGE',
      key: 'nextStage',
      render: (_, record) => getNextStage(record),
    },
    {
      title: 'APPROVAL LINK',
      key: 'approvalUrl',
      dataIndex: 'approvalUrl',
      render: (text, record, index) => (
        <div className="d-flex align-center" id={`approval-link-${index}`}>
          <Link
            to={`/${text?.split('/')?.slice(3).join('/')}`}
            id={`review-link-${index}`}
          >
            Review
          </Link>
          {record?.eqcStageHistory?.approvalType === APPROVAL_TYPE.ON_SITE && (
            <CommonTooltip title="On-site approval which has to be given from mobile application">
              <MobileOutlined id={`mobile-icon-${index}`} />
            </CommonTooltip>
          )}
        </div>
      ),
    },
  ];

  const onSearchChange = async (value) => {
    setTodoListFilter({ ...todoListFilter, skip: 0, search: value });
    setPaginationProp(initialPaginationValue);
    fetchTodoData({
      variables: { filter: { ...todoListFilter, skip: 0, search: value } },
    });
  };
  const handleRefetch = () => {
    fetchTodoData({
      variables: {
        filter: {
          ...todoListFilter,
          skip: approvalListData?.length,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: { field: 'createdAt', order: 'DESC' },
        },
      },
    });
  };
  const onProjectChange = (projectId) => {
    setPaginationProp({ ...paginationProp, current: 1 });
    setTodoListFilter({
      ...todoListFilter,
      projectId,
      skip: 0,
    });
    fetchTodoData({
      variables: {
        filter: {
          ...todoListFilter,
          projectId: Number(projectId),
          skip: 0,
        },
      },
    });
  };
  return (
    <>
      <Portal portalId="search-component">
        <div
          className={isDesktopViewport ? 'd-flex' : 'width-percent-100'}
          id="todo-search-portal"
        >
          <Row
            justify={isDesktopViewport ? 'end' : 'start'}
            className={`d-flex filter-search `}
            wrap
            gutter={isDesktopViewport ? [10, 10] : [0, 10]}
          >
            <Col
              span={isDesktopViewport ? null : 12}
              className="d-flex align-center"
            >
              <Checkbox
                id="rfi-checkbox"
                onChange={(event) => {
                  setIsRfi(event?.target?.checked);
                  setTodoListFilter({
                    ...todoListFilter,
                    skip: 0,
                    isRfi: event?.target?.checked,
                  });
                  setPaginationProp({ ...paginationProp, current: 1 });
                  fetchTodoData({
                    variables: {
                      filter: {
                        ...todoListFilter,
                        isRfi: event?.target?.checked,
                      },
                    },
                  });
                }}
                checked={isRfi}
              >
                RFI
              </Checkbox>
            </Col>
            <Col span={isDesktopViewport ? null : 24}>
              <CommonDropdown
                id="project-filter-dropdown"
                allowClear
                placeholder="Project"
                className={`instruction-selector dropdown-width-auto ${
                  isDesktopViewport ? 'width-200' : 'width-percent-100'
                } `}
                onChange={onProjectChange}
                query={GET_PROJECT_DROPDOWN_LIST}
                variables={{ filter: { todo: true } }}
                fetchPolicy="network-only"
                responsePath="projectDropdownList.data"
                valuePath="id"
                labelPath="name"
                optionKey="project"
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={false}
              />
            </Col>
            <Col span={isDesktopViewport ? null : 24}>
              <SearchComponent
                id="search-container-id"
                getData={onSearchChange}
              />
            </Col>
          </Row>
        </div>
      </Portal>
      {showModal && (
        <ThankyouModal
          setShowModal={setShowModal}
          showModal={showModal}
          loading={loading}
          count={paginationProp?.total}
          handleClick={() => {
            removeHistoryStateData(navigate, location, 'showThankyouModal');
          }}
        />
      )}
      {isDesktopViewport ? (
        <CommonTable
          id="todo-desktop-table"
          loadingData={loading}
          columns={columns}
          data={approvalListData || []}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(obj) => obj?.id}
        />
      ) : (
        <InfiniteScrollHandler
          id="todo-mobile-scroll"
          scrollFlag={scrollFlag}
          loading={loading}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          dataLength={approvalListData?.length}
          skeletonRows={columns?.length - 2}
        >
          {map(approvalListData, (todo, index) => {
            return (
              <CommonCard key={todo?.id} id={`todo-card-${index}`}>
                <div
                  className="common-card d-flex"
                  id={`todo-card-content-${index}`}
                >
                  <div
                    className="mr-5 fw-medium"
                    id={`todo-index-mobile-${index}`}
                  >
                    {index + 1}.
                  </div>
                  <div id={`todo-details-${index}`}>
                    <div className="card-header fw-medium">
                      <span>{todo?.eqc?.project?.name}</span>
                    </div>
                    <div className="card-content text-secondary">
                      <br />
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Location:</span>
                        {todo?.eqc?.eqcName}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Checklist:</span>
                        {todo?.eqc?.projectEqcTypeName || '-'}
                      </div>
                      <div className="d-flex mb-15">
                        <span className="fw-medium mr-5">
                          Stage(Approval Pending):
                        </span>
                        {getStageNameAndStatus(todo?.eqcStageHistory) || '-'}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Approver:</span>
                        {getApproverName(approvalListData, todo)}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Requested by:</span>
                        {todo?.approvalSender?.name || '-'}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Next stage:</span>
                        {getNextStage(todo)}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Approval link:</span>
                        <span>
                          <Link to={todo?.approvalUrl}>Review</Link>
                          {todo?.eqcStageHistory?.approvalType ===
                            APPROVAL_TYPE.ON_SITE && (
                            <CommonTooltip title="On-site approval which has to be given from mobile application">
                              <MobileOutlined />
                            </CommonTooltip>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CommonCard>
            );
          })}
        </InfiniteScrollHandler>
      )}
    </>
  );
};

export default TodoList;
